'use client'

import { useMemo, useState } from 'react'
import Image from 'next/image'

import type { Agent } from '@/lib/types/agents'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/hooks/use-i18n'

const PLACEHOLDER_COVER = '/assets/private-tools/cover/placeholder.svg'
const PLACEHOLDER_AVATAR = '/assets/private-tools/icons/placeholder.svg'

function resolveLabel(t: (key: string) => string, key: string, fallback: string) {
  const label = t(key)
  return label === key ? fallback : label
}

function plateTypeBadgeClass(plateType?: string | null) {
  switch (plateType) {
    case 'AIGC':
      return 'border-transparent bg-blue-600 text-white'
    case 'KOL':
      return 'border-transparent bg-emerald-600 text-white'
    case 'Flow':
      return 'border-transparent bg-amber-500 text-white'
    case 'Shop':
      return 'border-transparent bg-rose-600 text-white'
    case 'quntool':
      return 'border-transparent bg-indigo-600 text-white'
    case 'Common':
      return 'border-transparent bg-muted text-foreground'
    default:
      return 'border-transparent bg-muted text-foreground'
  }
}

function plateBadgeClass(plate?: number | null) {
  if (plate === 1) return 'border-transparent bg-emerald-600 text-white'
  if (plate === 2) return 'border-transparent bg-amber-500 text-white'
  return 'border-transparent bg-muted text-foreground'
}

export function AgentCard({
  agent,
  onLaunch,
  onDownload,
}: {
  agent: Agent
  onLaunch: (agent: Agent) => void
  onDownload: (agent: Agent) => void
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [coverSrc, setCoverSrc] = useState(agent.cover || PLACEHOLDER_COVER)
  const [avatarSrc, setAvatarSrc] = useState(agent.avatar || PLACEHOLDER_AVATAR)

  const plateTypeLabel = useMemo(() => {
    if (!agent.plate_type) return null
    return resolveLabel(t, `agents.plateType.${agent.plate_type}`, agent.plate_type)
  }, [agent.plate_type, t])

  const plateLabel = useMemo(() => {
    if (!agent.plate) return null
    return resolveLabel(t, `agents.plate.${agent.plate}`, String(agent.plate))
  }, [agent.plate, t])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setOpen(true)
        }}
        className={cn(
          'bg-card border border-border rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ease-out',
          'flex flex-col',
          'hover:shadow-xl hover:-translate-y-0.5 hover:scale-[1.01]'
        )}
      >
        <div className="p-3 flex-1">
          <div className="relative w-full aspect-[2/1] bg-muted rounded-lg overflow-hidden">
            <Image
              src={coverSrc}
              alt={agent.name}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 25vw"
              className="object-cover"
              onError={() => setCoverSrc(PLACEHOLDER_COVER)}
            />
          </div>

          <div className="mt-3 flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 text-base font-medium leading-snug text-foreground">
              <Image
                src={avatarSrc}
                alt={agent.name}
                width={24}
                height={24}
                className="rounded-[4px] object-contain shrink-0"
                onError={() => setAvatarSrc(PLACEHOLDER_AVATAR)}
              />
              <span className="line-clamp-1">{agent.name}</span>
            </div>

            {!agent.free && (
              <span className="text-xs font-medium bg-gradient-to-r from-[#876c36] to-[#cac59c] bg-clip-text text-transparent shrink-0">
                {t('agents.premium')}
              </span>
            )}
          </div>

          <p className="mt-1 text-xs text-muted-foreground leading-relaxed line-clamp-3 break-words">
            {agent.description}
          </p>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {plateTypeLabel && (
              <Badge className={plateTypeBadgeClass(agent.plate_type)}>{plateTypeLabel}</Badge>
            )}
            {plateLabel && (
              <Badge className={plateBadgeClass(agent.plate)}>{plateLabel}</Badge>
            )}
          </div>
        </div>

        <div className="h-10 border-t border-border bg-muted/40 hover:bg-accent transition-colors flex items-center justify-between px-4">
          <span className="text-sm text-muted-foreground">{t('agents.viewDetail')}</span>
          <div className="flex items-center gap-2">
            {agent.launch_type == 'download' && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="cursor-pointer hover:scale-105 transition-transform"
                onClick={(e) => {
                  e.stopPropagation()
                  onDownload(agent)
                }}
              >
                {t('agents.download')}
              </Button>
            )}

            {agent.launch_info?.login_url && (
              <Button
                type="button"
                size="sm"
                variant="default"
                className="cursor-pointer hover:scale-105 transition-transform bg-green-600 hover:bg-green-700 text-white"
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(agent.launch_info?.login_url || '', '_blank')
                }}
              >
                {t('agents.login')}
              </Button>
            )}

            {agent.launch_info?.url && (agent.launch_type == 'inner' || agent.launch_type == 'external') && (
              <Button
                type="button"
                size="sm"
                className="cursor-pointer hover:scale-105 transition-transform"
                onClick={(e) => {
                  e.stopPropagation()
                  onLaunch(agent)
                }}
              >
                {t('agents.launch')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{agent.name}</DialogTitle>
          <DialogDescription>{agent.description}</DialogDescription>
        </DialogHeader>

        {(agent.summary?.length > 0 || agent.features?.length > 0) && (
          <div className="space-y-4">
            {agent.summary?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">{t('agents.details.summary')}</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  {agent.summary.map((item, idx) => (
                    <li key={`${agent.id}-summary-${idx}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {agent.features?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">{t('agents.details.features')}</h3>
                <div className="flex flex-wrap gap-2">
                  {agent.features.map((feature) => (
                    <Badge key={`${agent.id}-${feature}`} variant="outline">
                      {feature}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
